import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, Circle, Loader2, Copy, ExternalLink, Share2, ChevronDown, Compass, ArrowRight,
} from "lucide-react";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HandoffColumns = ({ division }) => (
  <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="playbook-division">
    <div className="rounded-2xl border border-teal/30 bg-teal-light/20 p-4">
      <p className="text-[11px] uppercase tracking-widest text-teal font-bold">Yours</p>
      <ul className="mt-2.5 space-y-1.5">
        {division.officer.map((l) => (
          <li key={l} className="flex gap-2 text-[13px] text-ink-700 leading-relaxed">
            <ArrowRight size={13} className="shrink-0 mt-1 text-teal" />
            {l}
          </li>
        ))}
      </ul>
    </div>
    <div className="rounded-2xl border border-linen-border bg-linen/50 p-4">
      <p className="text-[11px] uppercase tracking-widest text-ink-500 font-bold">Ours</p>
      <ul className="mt-2.5 space-y-1.5">
        {division.grc.map((l) => (
          <li key={l} className="flex gap-2 text-[13px] text-ink-600 leading-relaxed">
            <Check size={13} className="shrink-0 mt-1 text-ink-400" />
            {l}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const PlaybookSteps = ({ steps, done = [], onToggle, readOnly = false }) => (
  <ol className="mt-6 space-y-3" data-testid="playbook-steps">
    {steps.map((s, i) => {
      const isDone = done.includes(s.key);
      return (
        <li
          key={s.key}
          data-testid={`playbook-step-${s.key}`}
          className={`rounded-2xl border p-4 sm:p-5 transition-colors ${
            isDone ? "border-teal/40 bg-teal-light/15" : "border-linen-border bg-white"
          }`}
        >
          <div className="flex items-start gap-4">
            {readOnly ? (
              <span className="shrink-0 mt-0.5 w-7 h-7 rounded-full border border-ink-300 text-ink-400 flex items-center justify-center text-xs font-bold tabular-nums">
                {i + 1}
              </span>
            ) : (
              <button
                onClick={() => onToggle(s.key)}
                data-testid={`playbook-toggle-${s.key}`}
                aria-label={isDone ? `Mark ${s.label} not done` : `Mark ${s.label} done`}
                className={`shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center border transition-all ${
                  isDone
                    ? "bg-teal border-teal text-white"
                    : "border-ink-300 text-ink-300 hover:border-teal hover:text-teal"
                }`}
              >
                {isDone ? <Check size={15} strokeWidth={3} /> : <Circle size={8} fill="currentColor" />}
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-[11px] uppercase tracking-widest text-teal font-bold">
                  Step {i + 1}
                </span>
                <span className="text-[11px] text-ink-400">{s.time}</span>
              </div>
              <h4 className={`mt-1 font-heading font-medium text-lg ${isDone ? "text-ink-400" : "text-navy"}`}>
                {s.label}
              </h4>
              <p className="mt-1.5 text-[14px] text-ink-600 leading-relaxed">{s.why}</p>

              <div className="mt-3.5 flex flex-wrap gap-2">
                {s.copy && (
                  <button
                    onClick={async () => {
                      const ok = await copyText(s.copy.text);
                      toast[ok ? "success" : "error"](ok ? "Copied — paste and edit the name" : "Couldn't copy that");
                    }}
                    data-testid={`playbook-copy-${s.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-navy hover:bg-navy-light text-white text-xs font-semibold px-4 py-2 transition-colors"
                  >
                    <Copy size={13} /> {s.copy.label}
                  </button>
                )}
                {s.links.map((l) => (
                  <a
                    key={l.url}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`playbook-link-${s.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-linen-border hover:border-teal text-navy text-xs font-semibold px-4 py-2 transition-colors"
                  >
                    {l.label} <ExternalLink size={12} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </li>
      );
    })}
  </ol>
);

export const RecruitPlaybookCard = ({ authHeader }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/mlo/playbook`, { headers: authHeader() });
      setData(d);
    } catch {
      setData(null);
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (key) => {
    const next = data.steps_done.includes(key)
      ? data.steps_done.filter((k) => k !== key)
      : [...data.steps_done, key];
    setData((d) => ({ ...d, steps_done: next, percent: Math.round((next.length * 100) / d.total) }));
    try {
      await axios.post(
        `${API}/mlo/playbook/progress`,
        { key, done: next.includes(key) },
        { headers: authHeader() },
      );
    } catch {
      toast.error("Couldn't save that — try again.");
      load();
    }
  };

  const share = async () => {
    setSharing(true);
    try {
      const { data: res } = await axios.post(`${API}/mlo/playbook/share`, {}, { headers: authHeader() });
      const ok = await copyText(res.url);
      toast[ok ? "success" : "error"](
        ok ? "Share link copied — send it to whoever does your recruiting" : "Couldn't copy the link",
      );
      setData((d) => ({ ...d, share_token: res.token }));
    } catch {
      toast.error("Couldn't create that link.");
    } finally {
      setSharing(false);
    }
  };

  if (!data) return null;

  const { links, steps, steps_done: done, total, percent } = data;

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft overflow-hidden" data-testid="recruit-playbook-card">
      <div className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal font-bold">
              <Compass size={13} /> Recruiting playbook
            </div>
            <h3 className="mt-1 font-heading font-medium text-xl text-navy">How to sign up a partner</h3>
            <p className="mt-1 text-sm text-ink-500">
              Six steps. You make the ask — we handle the application, the agreement and their first week,
              then hand them back to you.
            </p>
          </div>
          <span className="text-sm text-ink-500 tabular-nums shrink-0" data-testid="playbook-progress">
            {done.length} of {total}
          </span>
        </div>

        <div className="mt-4 h-2 rounded-full bg-linen overflow-hidden">
          <div
            className="h-full rounded-full bg-teal"
            style={{ width: `${percent}%`, transition: "width .6s cubic-bezier(.22,1,.36,1)" }}
          />
        </div>

        {/* The branded link, always visible — it's the one thing they need at hand */}
        <div className="mt-5 rounded-2xl bg-navy p-5" data-testid="playbook-apply-link">
          <p className="text-[11px] uppercase tracking-widest text-lime font-bold">Your partner application link</p>
          <p className="mt-2 font-mono text-[12.5px] text-white/90 break-all">{links.apply_link}</p>
          <p className="mt-2.5 text-[12.5px] text-white/60 leading-relaxed">
            Everyone who applies through this lands in your pipeline with your name on it. Send this one —
            not the generic application — or you won't get the credit.
          </p>
          <div className="mt-3.5 flex flex-wrap gap-2">
            <button
              onClick={async () => {
                const ok = await copyText(links.apply_link);
                toast[ok ? "success" : "error"](ok ? "Your partner application link is copied" : "Couldn't copy that");
              }}
              data-testid="playbook-copy-apply-link"
              className="inline-flex items-center gap-1.5 rounded-full bg-lime text-navy-deep text-xs font-bold px-4 py-2 hover:brightness-105 transition-all"
            >
              <Copy size={13} /> Copy my link
            </button>
            <a
              href={links.apply_link}
              target="_blank"
              rel="noreferrer"
              data-testid="playbook-preview-apply-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/25 text-white text-xs font-semibold px-4 py-2 hover:bg-white/10 transition-colors"
            >
              See what they see <ExternalLink size={12} />
            </a>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="playbook-expand"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-teal transition-colors"
          >
            {open ? "Hide the six steps" : "Show me the six steps"}
            <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <button
            onClick={share}
            disabled={sharing}
            data-testid="playbook-share"
            className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-teal transition-colors disabled:opacity-60"
          >
            {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
            Share with my team
          </button>
        </div>

        {open && (
          <>
            <HandoffColumns division={data.division} />
            <PlaybookSteps steps={steps} done={done} onToggle={toggle} />
          </>
        )}
      </div>
    </div>
  );
};

export default RecruitPlaybookCard;
